import React, { useMemo, useCallback } from 'react';
import { View, Text, Pressable, Platform, useWindowDimensions } from 'react-native';
import { Info, Menu, Table2, Trophy } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { useAnalytics } from '../../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../src/analytics/events';
import { ANALYTICS_MENU_ITEMS } from '../../../src/analytics/analytics.constants';
import { useWorldCupWelcomeMenuStore } from '../../../src/worldcup/stores/worldCupWelcomeMenuStore';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { getWorldCupScreenHeaderStyles } from './WorldCupScreenHeader.styles';
import {
  STANDINGS_MODAL_COPY,
  STANDINGS_VIEWS,
  type StandingsModalView,
} from '../WorldCupStandingsModal/worldCupStandingsModal.constants';
import { getWorldCupScreen } from '../../../src/worldcup/helpers/wcBrand.helper';
import {
  WORLD_CUP_HEADER_ICON_SIZE,
  WORLD_CUP_HEADER_ICON_SIZE_NARROW,
  WORLD_CUP_HEADER_TROPHY_SIZE,
  WORLD_CUP_HEADER_TROPHY_SIZE_NARROW,
} from './WorldCupScreenHeader.constants';
import { XpHeaderCluster } from '../../../src/economy/components/XpHeaderCluster';

interface WorldCupScreenHeaderProps {
  /** Opens the standings modal on the requested tab (group table or knockout bracket). */
  onStandingsPress: (view: StandingsModalView) => void;
  onMenuPress: () => void;
}

export const WorldCupScreenHeader: React.FC<WorldCupScreenHeaderProps> = ({
  onStandingsPress,
  onMenuPress,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const { themeMode } = useThemeContext();
  const { track } = useAnalytics();
  const { width } = useWindowDimensions();
  const isNarrow = Platform.OS !== 'web' || width < 768;
  const openWorldCupRulesFromMenu = useWorldCupWelcomeMenuStore((s) => s.openWorldCupRulesFromMenu);
  const styles = useMemo(
    () => getWorldCupScreenHeaderStyles(theme, themeMode, isNarrow),
    [theme, themeMode, isNarrow]
  );
  const worldCupScreen = useMemo(() => getWorldCupScreen(), []);
  const headerIconColor = themeMode === 'normal' ? theme.white : theme.primary;
  const iconSize = isNarrow ? WORLD_CUP_HEADER_ICON_SIZE_NARROW : WORLD_CUP_HEADER_ICON_SIZE;
  const trophySize = isNarrow ? WORLD_CUP_HEADER_TROPHY_SIZE_NARROW : WORLD_CUP_HEADER_TROPHY_SIZE;

  const handleWorldCupRulesPress = useCallback(() => {
    track(ANALYTICS_EVENTS.MENU_ITEM_CLICKED, {
      menu_item: ANALYTICS_MENU_ITEMS.WORLD_CUP_RULES,
      source: 'worldcup_header',
    });
    openWorldCupRulesFromMenu();
  }, [openWorldCupRulesFromMenu, track]);

  // Tapping the avatar opens the self-profile (specs/profile-page); switching moved to the menu.
  const handleAvatarPress = useCallback(() => router.push('/app/profile'), [router]);
  // Tapping the rank badge opens the Rank Ladder on top of the current screen (specs/rank-ladder).
  const handleRankPress = useCallback(() => router.push('/app/rank-ladder'), [router]);

  return (
    <View style={styles.screenHeader}>
      <View style={styles.screenHeaderLeadFlex}>
        <XpHeaderCluster
          onAvatarPress={handleAvatarPress}
          onRankPress={handleRankPress}
          testID="worldcup-header-avatar"
        />
      </View>

      <View style={styles.screenHeaderTitleCluster}>
        <View style={styles.screenHeaderIcon}>
          <Trophy size={trophySize} color={headerIconColor} strokeWidth={2.2} />
        </View>
        <View style={styles.screenHeaderDivider} />
        <Text style={styles.screenTitleText} numberOfLines={1} ellipsizeMode="tail">
          {worldCupScreen.TITLE}
        </Text>
        <Text style={styles.screenTitleYear} numberOfLines={1}>
          {worldCupScreen.YEAR}
        </Text>
      </View>

      <View style={styles.screenHeaderTrailFlex}>
        <Pressable
          style={({ pressed, hovered }) => [
            styles.headerSideButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.headerSideButtonPressed,
            hovered && Platform.OS === 'web' && styles.headerSideButtonHover,
          ]}
          onPress={() => onStandingsPress(STANDINGS_VIEWS.GROUPS)}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          testID="worldcup-header-standings"
          accessibilityRole="button"
          accessibilityLabel={STANDINGS_MODAL_COPY.ACCESSIBILITY_OPEN_GROUPS}
        >
          <Table2 size={iconSize} color={headerIconColor} strokeWidth={2.2} />
        </Pressable>
        <Pressable
          style={({ pressed, hovered }) => [
            styles.headerSideButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.headerSideButtonPressed,
            hovered && Platform.OS === 'web' && styles.headerSideButtonHover,
          ]}
          onPress={handleWorldCupRulesPress}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          testID={worldCupScreen.HEADER_WORLD_CUP_RULES_TEST_ID}
          accessibilityRole="button"
          accessibilityLabel={worldCupScreen.HEADER_WORLD_CUP_RULES_ACCESSIBILITY}
        >
          <Info size={iconSize} color={headerIconColor} strokeWidth={2.2} />
        </Pressable>
        <Pressable
          style={({ pressed, hovered }) => [
            styles.headerSideButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.headerSideButtonPressed,
            hovered && Platform.OS === 'web' && styles.headerSideButtonHover,
          ]}
          onPress={onMenuPress}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          testID="worldcup-screen-hamburger-menu"
          accessibilityRole="button"
          accessibilityLabel={worldCupScreen.HEADER_OPEN_MENU_ACCESSIBILITY}
        >
          <Menu size={iconSize} color={theme.white} strokeWidth={2.2} />
        </Pressable>
      </View>
    </View>
  );
};
